import {
    ContainerOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EuroOutlined,
    FormOutlined,
    RollbackOutlined,
    SendOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Descriptions,
    Form,
    message,
    Modal,
    Popconfirm,
    Row,
    Spin,
    Table,
    Tabs,
    Typography,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { UNSAFE_LocationContext, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../common/api";
import PdfViewer from "../common/PdfViewer";
import {
    date2locale,
    datetime2locale,
    downloadFile,
    formatDateTime,
    toLocaleString,
} from "../common/common";
import Toolbar from "../common/Toolbar";
import { renderStatus } from "./Fines";
import Link from "antd/lib/typography/Link";
import { UserInfoContext } from "../FetchUserInfo";
import DebounceSelect from "../common/DebounceSelect";
import SelectUser from "../common/SelectUser";

const employees = (emps) => (
    <Table
        rowKey="id"
        size="small"
        scroll={{ y: 500 }}
        pagination={false}
        dataSource={emps}
        columns={[{ key: "fullName", title: "Nominativo", dataIndex: "fullName" }]}
    />
);

const attachments = (atts, setSelectedAttachment) => (
    <Table
        rowKey="id"
        dataSource={atts}
        size="small"
        tableLayout="fixed"
        scroll={{ y: 500 }}
        pagination={false}
        columns={[
            {
                key: "ref",
                title: "Descrizione",
                width: 400,
                render: (item) => (
                    <Typography.Link
                        onClick={() => setSelectedAttachment(`attachment/${item.id}/file`)}
                    >
                        {item.ref}
                    </Typography.Link>
                ),
            },
            {
                key: "download",
                render: (item) => (
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={() => downloadFile(`attachment/${item.id}/file`)}
                    >
                        Scarica
                    </Button>
                ),
            },
        ]}
    />
);

const charges = (cs) => (
    <Table
        rowKey="id"
        dataSource={cs}
        size="small"
        tableLayout="fixed"
        scroll={{ y: 500 }}
        pagination={false}
        columns={[
            { key: "fullName", title: "Nominativo", dataIndex: "fullName", width: "80%" },
            {
                key: "amount",
                title: "Importo",
                dataIndex: "amount",
                render: (amount) => toLocaleString(amount ?? 0),
            },
        ]}
    />
);

const ChangeAssignee = ({ fine, open, closeFn, onFinish }) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (open) {
            console.log("Reset");
            form.resetFields();
        }
    }, [open]);
    return (
        <Modal
            open={open}
            title="Cambia assegnatario"
            okButtonProps={{ htmlType: "submit", form: "fineSelectUser", loading }}
            onCancel={closeFn}
        >
            <Form
                id="fineSelectUser"
                form={form}
                onFinish={(form) => {
                    axiosInstance
                        .post(`fine/${fine}/assignee`, form)
                        .then(onFinish)
                        .then(closeFn)
                        .finally(() => setLoading(false));
                }}
            >
                <Form.Item name="user">
                    <SelectUser labelInValue={false} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default function () {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const userInfo = useContext(UserInfoContext);
    const { id } = useParams();
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [reminderLoading, setReminderLoading] = useState(false);
    const [changeAssigneeOpen, setChangeAssigneeOpen] = useState(false);

    const fetch = () => {
        setLoading(true);
        axiosInstance
            .get(`fine/${id}`)
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(fetch, []);
    const userHasFullAccess = userInfo.permissions?.includes("fine.fullAccess");
    const userAssignedTo = data.assignedTo?.id === userInfo.id;
    const userCreatedBy = data.createdBy?.id === userInfo.id;

    return (
        <Spin spinning={loading}>
            <ChangeAssignee
                fine={id}
                open={changeAssigneeOpen}
                closeFn={() => setChangeAssigneeOpen(false)}
                onFinish={fetch}
            />
            <PdfViewer url={selectedAttachment} onClose={() => setSelectedAttachment()} />
            <Toolbar
                topDivider={false}
                extra={() => (
                    <>
                        {data.status === "TO_MANAGE" &&
                            (userAssignedTo || userCreatedBy || userHasFullAccess) && (
                                <Button
                                    onClick={() => navigate("decision")}
                                    icon={<FormOutlined />}
                                >
                                    Gestisci
                                </Button>
                            )}
                        {data.status === "TO_MANAGE" && (userCreatedBy || userHasFullAccess) && (
                            <Button
                                onClick={() => setChangeAssigneeOpen(true)}
                                icon={<FormOutlined />}
                            >
                                Cambia assegnatario
                            </Button>
                        )}
                        {data.status === "TO_MANAGE" && (userCreatedBy || userHasFullAccess) && (
                            <Button
                                onClick={() => {
                                    setReminderLoading(true);
                                    axiosInstance
                                        .post(`fine/${data.id}/reminder`)
                                        .then(() => {message.success("Sollecito inviato")})
                                        .finally(() => setReminderLoading(false));
                                }}
                                icon={<SendOutlined />}
                                loading={reminderLoading}
                            >
                                Sollecita
                            </Button>
                        )}
                        {data.status === "MANAGED" && (userCreatedBy || userHasFullAccess) && (
                            <Button onClick={() => navigate("charge")} icon={<EuroOutlined />}>
                                Genera addebito
                            </Button>
                        )}
                        {data.status === "MANAGED" && (userCreatedBy || userHasFullAccess) && (
                            <Button
                                onClick={() => navigate("integrate")}
                                icon={<RollbackOutlined />}
                            >
                                Richiedi integrazione
                            </Button>
                        )}
                        {data.status &&
                            !["CHARGE_CREATED", "ARCHIVED_MANAGED", "ARCHIVED_TO_MANAGE"].includes(
                                data.status,
                            ) &&
                            (userCreatedBy || userHasFullAccess) && (
                                <Button
                                    icon={<ContainerOutlined />}
                                    onClick={() => {
                                        setLoading(true);
                                        axiosInstance
                                            .post(`/fine/${id}/archive`)
                                            .then(fetch)
                                            .finally(() => setLoading(false));
                                    }}
                                >
                                    Archivia
                                </Button>
                            )}

                        {data.status &&
                            ["ARCHIVED_MANAGED", "ARCHIVED_TO_MANAGE"].includes(data.status) &&
                            (userCreatedBy || userHasFullAccess) && (
                                <Button
                                    icon={<ContainerOutlined />}
                                    onClick={() => {
                                        setLoading(true);
                                        axiosInstance
                                            .post(`/fine/${id}/unarchive`)
                                            .then(fetch)
                                            .finally(() => setLoading(false));
                                    }}
                                >
                                    Annulla archiviazione
                                </Button>
                            )}
                        {(userCreatedBy || userHasFullAccess) && (
                            <Popconfirm
                                title={`Elimina multa`}
                                description={`Verrà eliminata la multa e tutti gli elementi associati. Non è un operazione reversibile, procedere?`}
                                okText="Si"
                                cancelText="No"
                                onConfirm={() =>
                                    axiosInstance.delete(`fine/${id}`).then(() => navigate(-1))
                                }
                            >
                                <Button danger icon={<DeleteOutlined />}>
                                    Elimina
                                </Button>
                            </Popconfirm>
                        )}
                    </>
                )}
            />
            <Row>
                <Col span={18} style={{ paddingRight: 20 }}>
                    <Descriptions
                        colon={false}
                        layout="vertical"
                        size="small"
                        column={3}
                        style={{ marginTop: 20 }}
                    >
                        <Descriptions.Item label="Num verbale">{data.number}</Descriptions.Item>
                        <Descriptions.Item label="Data">{date2locale(data.date)}</Descriptions.Item>
                        <Descriptions.Item label="Targa">{data.licensePlate}</Descriptions.Item>
                        <Descriptions.Item label="Descrizione" span={3}>
                            {data.description}
                        </Descriptions.Item>
                        <Descriptions.Item label="Importo ridotto">
                            {toLocaleString(data.reducedAmount ?? 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Importo intero">
                            {toLocaleString(data.fullAmount ?? 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Importo pagato">
                            {{ REDUCED: "Ridotto", FULL: "Intero" }[data.amountPaid]}
                        </Descriptions.Item>
                        <Descriptions.Item label="Assegnato a">
                            {data.assignedTo?.fullName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Gestito da">
                            {data.managedBy?.fullName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Stato" span={2}>
                            {renderStatus(data.status)}
                        </Descriptions.Item>
                    </Descriptions>
                    <Tabs
                        style={{ marginTop: 32 }}
                        items={[
                            {
                                key: "employees",
                                label: "Responsabili",
                                children: employees(data.employees ?? []),
                            },
                            {
                                key: "attachments",
                                label: "Allegati",
                                children: attachments(
                                    data.attachments ?? [],
                                    setSelectedAttachment,
                                ),
                            },
                            {
                                key: "charges",
                                label: "Addebiti",
                                children: charges(data.charges ?? []),
                            },
                        ]}
                    />
                </Col>
                <Col span={6} style={{ background: "#EEEEEE" }}>
                    <div
                        style={{
                            background: "white",
                            padding: 8,
                            textAlign: "center",
                            fontSize: "1.2em",
                            fontWeight: "bold",
                        }}
                    >
                        Commenti
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            rowGap: 8,
                            marginTop: 8,
                            marginBottom: 8,
                        }}
                    >
                        {data.comments
                            ?.sort((a, b) => b.id - a.id)
                            .map((c) => (
                                <Card
                                    key={c.id}
                                    style={{ marginLeft: 20, marginRight: 20 }}
                                    bodyStyle={{ padding: 16 }}
                                >
                                    <Typography.Text strong>{c.fullName}</Typography.Text>
                                    <div
                                        style={{
                                            whiteSpace: "pre-line",
                                            marginTop: 5,
                                            marginBottom: 5,
                                            lineHeight: "1.3",
                                        }}
                                    >
                                        {c.message}
                                    </div>

                                    <div style={{ textAlign: "end" }}>
                                        <Typography.Text
                                            type="secondary"
                                            style={{ fontSize: "0.9em" }}
                                        >
                                            {formatDateTime(c.ts)}
                                        </Typography.Text>
                                    </div>
                                </Card>
                            ))}
                    </div>
                </Col>
            </Row>
        </Spin>
    );
}
